import { useEffect, useState } from "react";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { fetchGoals } from "../../../features/goals/api";
import { t } from "../../../locales/t";

/**
 * The goals a member chose at onboarding, shown on Edit Profile.
 *
 * Sits alongside the demographic sections and behaves the same way: current
 * values, then an Edit button onto the dedicated editor route. Goals were the
 * one thing onboarding collected that the profile never showed, so a member had
 * no way to see what they had picked, let alone change it.
 *
 * It fetches rather than reading `localProfile`, because goals are not part of
 * the legacy profile payload — they live behind `GET /v2/me/goals`. That makes
 * it independent of the surrounding page's data, so it degrades on its own
 * instead of taking Edit Profile down with it.
 */

const LABEL_STYLE = {
  fontSize: { xs: "15px", sm: "16px" },
  fontWeight: "400",
  color: "#2A4876",
  paddingLeft: "16px",
};

const EDIT_BUTTON_STYLE = {
  border: "1px solid #AAB6C8",
  padding: "10px 26px",
  borderRadius: "24px",
  textTransform: "none",
  color: "#152134",
  flexShrink: 0,
};

const GoalsSection = () => {
  const navigate = useNavigate();
  const [goals, setGoals] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const current = await fetchGoals();
        if (!cancelled) setGoals(current.goals);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // A failed fetch hides the section rather than showing an error. Goals are one
  // part of a long page, and a member who came here to change their photo should
  // not be met with a failure about something else.
  if (failed) return null;

  return (
    <Box sx={{ marginTop: "24px" }}>
      <Typography
        sx={{ fontWeight: 600, color: "#152134", marginBottom: "8px" }}
      >
        Your goals
      </Typography>

      {/* Wraps rather than overflowing: the label column and the button share a
          row on desktop and stack on a narrow screen. */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
        }}
      >
        <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
          {goals === null && (
            <Skeleton variant="text" width={220} height={28} />
          )}

          {goals?.length === 0 && (
            <Typography sx={{ ...LABEL_STYLE, color: "#6B7A90" }}>
              No goals chosen yet.
            </Typography>
          )}

          {goals?.map((goal) => (
            <Typography key={goal} sx={LABEL_STYLE}>
              <span
                style={{ color: "black", fontSize: "20px", marginRight: "8px" }}
              >
                •
              </span>
              {t(`onboarding.goals.options.${goal}.label`)}
            </Typography>
          ))}
        </Box>

        <Button
          style={EDIT_BUTTON_STYLE}
          onClick={() => navigate("/profile/edit-goals")}
        >
          Edit
        </Button>
      </Box>
    </Box>
  );
};

export default GoalsSection;
